import type { BookingConfirmationCarRental } from '@massilia/queries'

import { format, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'
import { Calendar, Car, MapPin } from 'lucide-react'

import { centsToEuros, formatPrice } from '@/utils/format-price'

interface CarRentalsSectionProps {
  carRentals: BookingConfirmationCarRental[]
}

export function CarRentalsSection({ carRentals }: CarRentalsSectionProps) {
  if (carRentals.length === 0) return null

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-gray-light-200 bg-white p-4 md:p-6">
      <div className="text-lg leading-7 font-semibold text-gray-light-900 sm:text-2xl">
        Locations de véhicules
      </div>

      {carRentals.map((rental, i) => (
        <div
          key={i}
          className="flex flex-col gap-3 rounded-xl border border-gray-light-200 p-3 md:p-4"
        >
          {/* Company + location + price */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex size-9 items-center justify-center rounded-lg bg-sky-50">
                <Car className="size-5 text-sky-600" />
              </div>
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-gray-light-900">{rental.company}</span>
                <span className="flex items-center gap-1 text-xs font-medium text-gray-light-600">
                  <MapPin className="size-3" />
                  {rental.location}
                </span>
              </div>
            </div>
            <span className="text-sm font-bold text-gray-light-900">
              {formatPrice(centsToEuros(rental.price))}
            </span>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-0.5">
              <span className="flex items-center gap-1 text-[10px] font-semibold tracking-wider text-gray-light-500 uppercase">
                <Calendar className="size-3" /> Prise en charge
              </span>
              <span className="text-gray-light-800 text-sm font-semibold">
                {formatDate(rental.pickUp)}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="flex items-center gap-1 text-[10px] font-semibold tracking-wider text-gray-light-500 uppercase">
                <Calendar className="size-3" /> Restitution
              </span>
              <span className="text-gray-light-800 text-sm font-semibold">
                {formatDate(rental.dropOff)}
              </span>
            </div>
          </div>

          {/* Details */}
          {rental.details && (
            <p className="text-xs leading-relaxed text-gray-light-600">{rental.details}</p>
          )}
        </div>
      ))}
    </div>
  )
}

function formatDate(isoDate: string): string {
  const date = parseISO(isoDate)
  return format(date, 'dd MMM yyyy', { locale: fr })
}
